import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import {
  Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, MenuItem, FormControl, InputLabel, FormHelperText,
  Typography, CircularProgress, Alert, Paper,
  Tooltip, Breadcrumbs, IconButton, useMediaQuery,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Popover, InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import SaveIcon from '@mui/icons-material/Save';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { FormNavigationProvider, NavTextField, NavLongField, NavDoubleField, NavSelect, NavDatePicker, NavAutocomplete } from '../components/FormNavigation';
import NavBanglaPhoneticInput from '../components/NavBanglaPhoneticInput';
import QuickGrouplessTagEditorDialog from '../components/QuickGrouplessTagEditorDialog';
import EmployeeFilter from '../components/EmployeeFilter';
import { useAppDispatch, useAppSelector } from '../store';
import { upsertEmployeeListItem, toListItem } from '../store/employeesSlice';
import { fetchEmployeeTags } from '../store/employeeTagsSlice';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import { getEmployee, putEmployee } from '../services/employeeDB';
import type { UIField } from '../types/applicationSettings';
import type { EmployeeRecord, HistoryEntry, EmployeeListItem } from '../types/employees';
import { buildEvalContext, evaluateDSL } from '../utils/calculatedValueEvaluator';
import { fetchCalculatedValueDefinitions } from '../store/applicationSettingsSlice';
import { useEmployeeRecords } from '../hooks/useEmployeeRecords';

// ─── Constants ───────────────────────────────────────────────────────────────
const ITEM_HEIGHT = 48;
const FORM_WIDTH_X = 720; // reference container width

// ─── Termination type definitions ────────────────────────────────────────────
const TERMINATION_TYPES = [
  { code: 1, label: 'Resignation', definition: 'The employee voluntarily chooses to leave the company, typically providing formal advance notice.' },
  { code: 2, label: 'Retirement', definition: 'The employee voluntarily and permanently exits the workforce, usually after reaching a specific age or career milestone.' },
  { code: 3, label: 'Job Abandonment', definition: 'The employee fails to report to work for consecutive days without notifying the employer, effectively quitting without notice.' },
  { code: 4, label: 'Termination for Cause', definition: 'The employer dismisses the employee due to severe misconduct, policy violations, or chronic poor performance.' },
  { code: 5, label: 'Layoff', definition: 'The employer involuntarily terminates the employee due to business reasons like restructuring or downsizing, rather than performance issues.' },
  { code: 6, label: 'End of Contract', definition: 'The employment automatically concludes because a predetermined temporary agreement or project timeline has expired without renewal.' },
  { code: 7, label: 'Medical Separation', definition: 'The employer separates the employee because they remain unable to return to work after exhausting all legally protected or company-provided medical leave.' },
  { code: 8, label: 'Disability Separation', definition: 'The employee is separated because a permanent physical or mental impairment prevents them from performing essential job functions, and no reasonable accommodation can be made.' },
  { code: 9, label: 'Death in Service', definition: 'The employment relationship is tragically terminated due to the passing of the employee while actively employed.' },
] as const;

function termTypeLabel(code: string | number): string {
  const n = Number(code);
  return TERMINATION_TYPES.find((t) => t.code === n)?.label ?? String(code);
}

// ─── Size mapping ─────────────────────────────────────────────────────────────
function gridSize(size: string): number {
  switch (size) {
    case '2': return 6;
    case '3': return 9;
    case '4': case 'large': return 12;
    default: return 3;
  }
}

// ─── Responsive size upgrade ──────────────────────────────────────────────────
// As container width shrinks, field sizes are bumped UP towards 4 (full width).
// X = FORM_WIDTH_X (full-size reference):
//   ≥ X          → original (1, 2, 3, 4)
//   65–100% of X → 1→2, 2→3, 3→4, 4→4
//   50–65%  of X → 1→3, 2→3, 3→4, 4→4
//   < 50%   of X → all→4
function effectiveSize(size: string, containerWidth: number): string {
  const s = size === 'large' ? 4 : (parseInt(size, 10) || 1);
  let e: number;
  if (containerWidth >= FORM_WIDTH_X) {
    e = s;
  } else if (containerWidth >= FORM_WIDTH_X * 0.75) {
    e = Math.min(4, s + 1);
  } else if (containerWidth >= FORM_WIDTH_X * 0.50) {
    e = s <= 2 ? 3 : 4;
  } else {
    e = 4;
  }
  return String(e);
}

// ─── History helpers ──────────────────────────────────────────────────────────
function isHistoryArray(v: unknown): v is HistoryEntry[] {
  return Array.isArray(v) && v.length > 0 && typeof (v[0] as any)?.date === 'string';
}

function toFlatStr(v: unknown): string {
  if (v == null) return '';
  if (Array.isArray(v)) return v.join(',');
  return String(v);
}

function toMonthStart(date: string): string {
  return date.slice(0, 7) + '-01';
}

function effectiveValue(entries: HistoryEntry[], date: string, unit: string): unknown {
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  let result: unknown = undefined;
  for (const e of sorted) {
    if (unit === 'month') {
      // Compare only YYYY-MM parts to stay within the same or past periods
      if (e.date.slice(0, 7) <= date.slice(0, 7)) result = e.value;
    } else {
      if (e.date <= date) result = e.value;
    }
  }
  return result;
}

function allHistoryDates(record: EmployeeRecord, historyFields: Set<string>, unit: string): string[] {
  const dates = new Set<string>();
  historyFields.forEach((key) => {
    const v = record[key];
    if (isHistoryArray(v)) {
      v.forEach((e) => {
        const d = unit === 'month' ? toMonthStart(e.date) : e.date;
        dates.add(d);
      });
    }
  });
  return Array.from(dates).sort();
}

function latestHistoryDate(record: EmployeeRecord, historyFields: Set<string>, unit: string): string {
  const dates = allHistoryDates(record, historyFields, unit);
  return dates[dates.length - 1] ?? '';
}

// -- removed old toMonthStart position --

// ─── Image conversion ─────────────────────────────────────────────────────────
async function fileToJpeg(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (blob) resolve(blob);
        else reject(new Error('Failed to convert image'));
      }, 'image/jpeg', 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}

// ─── Main component ───────────────────────────────────────────────────────────
const Employees: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [containerWidth, setContainerWidth] = useState(FORM_WIDTH_X);
  const dispatch = useAppDispatch();
  const employeeList = useAppSelector((s) => s.employees.list);
  const uiFields = useAppSelector((s) => s.applicationSettings.uiFields);
  const historyTrackingUnit = useAppSelector((s) => s.applicationSettings.globalSettings?.historyTrackingUnit ?? 'day');
  const selectedUnit = useAppSelector((s) => s.auth.selectedUnit);
  const employeeTags = useAppSelector((s) => s.employeeTags.tags);
  const calculatedValueDefinitions = useAppSelector((s) => s.applicationSettings.calculatedValueDefinitions);
  const dbKey = selectedUnit
    ? `cranberry-employees-${selectedUnit.tenant}-${selectedUnit.solution}-${selectedUnit.unit}`
    : 'cranberry-employees';
  const employeeRecords = useEmployeeRecords(dbKey);

  const [mode, setMode] = useState<'create' | 'edit' | null>(null);
  const [currentUUID, setCurrentUUID] = useState<string | null>(null);

  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [employeeRecord, setEmployeeRecord] = useState<EmployeeRecord | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');

  // Timeline scroll offset for fade effect
  const [tlScrollOffset, setTlScrollOffset] = useState(0);

  // Employee loading state
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  useEffect(() => {
    if (!loadingEmployee) {
      setShowLoadingOverlay(false);
      return;
    }
    const t = setTimeout(() => setShowLoadingOverlay(true), 150);
    return () => clearTimeout(t);
  }, [loadingEmployee]);
  const skipBlurRef = useRef(false);
  const pendingNavigateAfterLoadRef = useRef(false);

  // F2 Dialog
  const [f2DialogOpen, setF2DialogOpen] = useState(false);
  const [f2AnchorEl, setF2AnchorEl] = useState<HTMLElement | null>(null);
  const [f2SearchQuery, setF2SearchQuery] = useState('');
  const [f2SelectedIndex, setF2SelectedIndex] = useState(0);
  const f2ListRef = useRef<HTMLDivElement>(null);

  // Edit effective date dialog (create mode)
  const [editDateDialogOpen, setEditDateDialogOpen] = useState(false);
  const [editDateValue, setEditDateValue] = useState<Dayjs | null>(dayjs());

  // New version date dialog
  const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);
  const [newVersionDate, setNewVersionDate] = useState<Dayjs | null>(dayjs());

  // New-version confirmation dialog
  const [overrideConfirmOpen, setOverrideConfirmOpen] = useState(false);
  const [pendingSaveDate, setPendingSaveDate] = useState<string>('');
  const [pendingHistoryChanges, setPendingHistoryChanges] = useState<Array<{ field: UIField; oldValue: string; newValue: string }>>([]);
  const [diffExpanded, setDiffExpanded] = useState(false);

  // Photo / signature
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [signPreview, setSignPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<Blob | null>(null);
  const [signFile, setSignFile] = useState<Blob | null>(null);
  const [imageError, setImageError] = useState<string>('');

  const [deletePhoto, setDeletePhoto] = useState(false);
  const [deleteSign, setDeleteSign] = useState(false);

  const [saving, setSaving] = useState(false);
  const [historyDialogField, setHistoryDialogField] = useState<UIField | null>(null);
  const [historyDialogKey, setHistoryDialogKey] = useState<string | null>(null);
  const secondaryActiveRef = React.useRef<Set<string>>(new Set());
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);

  // Termination dialog
  const [terminationDialogOpen, setTerminationDialogOpen] = useState(false);
  const [termValues, setTermValues] = useState({ terminationType: '', terminationRemarks: '', terminationDate: '' });
  const [termErrors, setTermErrors] = useState<Record<string, string>>({});
  const [termSaving, setTermSaving] = useState(false);
  const termNavContainerRef = useRef<HTMLElement | null>(null);
  const termSaveButtonRef = useRef<HTMLButtonElement | null>(null);

  // Quick Groupless Tag Editor
  const [quickGrouplessTagEditorOpen, setQuickGrouplessTagEditorOpen] = useState(false);
  const [quickGrouplessTagEditorRoot, setQuickGrouplessTagEditorRoot] = useState<any>(null);
  const [quickGrouplessTagEditorTitle, setQuickGrouplessTagEditorTitle] = useState('');

  // Filter drawer
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filteredUUIDs, setFilteredUUIDs] = useState<string[] | null>(null);
  const [drawerWidth, setDrawerWidth] = useState(525);
  const isResizingRef = useRef(false);

  const navContainerRef = useRef<HTMLElement | null>(null);
  const formPanelRef = useRef<HTMLDivElement | null>(null);
  const saveButtonRef = useRef<HTMLButtonElement | null>(null);
  const employeeIdInputRef = useRef<HTMLInputElement | null>(null);
  // Tracks the last-active employee and their selected date so blur restores it
  const lastEditStateRef = useRef<{ internalId: string; date: string } | null>(null);

  const activeFields = useMemo(() => uiFields.filter((f) => f.active), [uiFields]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const historyFieldNames = React.useMemo(() => new Set(uiFields.filter((f) => f.historyEnabled).map((f) => f.name)), [uiFields]);

  // Set of tagCodes where the root tag has allowMultiChildSelection (used in save conversion)
  const multiSelectTagCodes = useMemo(() =>
    new Set(
      employeeTags
        .filter((t) => t.parentId === 0 && t.allowMultiChildSelection && t.tagCode)
        .map((t) => t.tagCode as string)
    ),
    [employeeTags]
  );

  // tagId string → fullTagTitle (for resolving values in dialogs)
  const tagTitleById = useMemo(() =>
    new Map(employeeTags.map((t) => [String(t.tagId), t.fullTagTitle ?? t.tagTitle])),
    [employeeTags]
  );

  // Returns { options, multi } for a given tag category field name (tagCode)
  const tagOptionsByCode = useMemo(() => {
    return (tagCode: string): { options: { value: string; label: string }[]; multi: boolean } => {
      const root = employeeTags.find((t) => t.tagCode === tagCode && t.parentId === 0);
      if (!root) return { options: [], multi: false };
      const descendants = employeeTags.filter((t) => t.dna[1] === root.tagId && t.tagId !== root.tagId);
      // Build parentIds WITHIN this category only to avoid cross-category contamination
      const descendantParentIds = new Set(descendants.map((t) => t.parentId));
      const filtered = root.considerSubParentAsTag
        ? descendants
        : descendants.filter((t) => !descendantParentIds.has(t.tagId));
      return {
        options: filtered.map((t) => ({ value: String(t.tagId), label: t.fullTagTitle ?? t.tagTitle })),
        multi: root.allowMultiChildSelection,
      };
    };
  }, [employeeTags]);

  const handleFilterChange = useCallback((uuids: string[] | null) => {
    setFilteredUUIDs(uuids);
  }, []);

  const handleDrawerResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    const startX = e.clientX;
    const startWidth = drawerWidth;
    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizingRef.current) return;
      // dragging left = startX - ev.clientX is positive = increase width
      const newWidth = Math.max(440, Math.min(1050, startWidth + (startX - ev.clientX)));
      setDrawerWidth(newWidth);
    };
    const onMouseUp = () => {
      isResizingRef.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [drawerWidth]);

  const drawerEmployees = useMemo(() => {
    if (filteredUUIDs === null) return employeeList;
    const uuidSet = new Set(filteredUUIDs);
    return employeeList.filter((e) => uuidSet.has(e.employeeUUID));
  }, [filteredUUIDs, employeeList]);

  const normalizeDate = useCallback(
    (date: string) => historyTrackingUnit === 'month' ? toMonthStart(date) : date,
    [historyTrackingUnit],
  );

  const formatTimelineDate = useCallback(
    (d: string) => historyTrackingUnit === 'month' ? dayjs(d).format('MMM YYYY').toUpperCase() : d,
    [historyTrackingUnit],
  );

  // ── Track form panel width for responsive field sizing ────────────────────
  // Re-runs when uiFields loads (panel unmounts during loading state, mounts after).
  useEffect(() => {
    const el = formPanelRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [uiFields.length]);

  // ── Focus employeeInternalId on mount (after fields render) ───────────────
  useEffect(() => {
    if (uiFields.length === 0) return;
    setTimeout(() => { employeeIdInputRef.current?.focus(); }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiFields.length]);

  // ── Load calculated value definitions once ────────────────────────────────
  useEffect(() => {
    dispatch(fetchCalculatedValueDefinitions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load employee photo/signature URLs ────────────────────────────────────
  const loadEmployeeImages = useCallback(async (uuid: string) => {
    setPhotoPreview(null);
    setSignPreview(null);
    const loadImage = async (type: 'photo' | 'sign') => {
      try {
        const key = `employee-images/${type}_${uuid}.jpg`;
        const res = await apiService.getDownloadUrl(key);
        if (type === 'photo') setPhotoPreview(res.data.url);
        else setSignPreview(res.data.url);
      } catch { /* not found */ }
    };
    loadImage('photo');
    loadImage('sign');
  }, []);

  useEffect(() => {
    if (!currentUUID) { setPhotoPreview(null); setSignPreview(null); }
  }, [currentUUID]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const historyDatesForRecord = useCallback(() => {
    if (!employeeRecord) return [];
    return allHistoryDates(employeeRecord, historyFieldNames, historyTrackingUnit);
  }, [employeeRecord, historyFieldNames, historyTrackingUnit]);

  const setFieldValue = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => { const e = { ...prev }; delete e[name]; return e; });
  };

  const flattenRecord = useCallback((record: EmployeeRecord, date: string): Record<string, string> => {
    const flat: Record<string, string> = {};
    for (const field of uiFields) {
      const v = record[field.name];
      if (field.historyEnabled && isHistoryArray(v)) {
        flat[field.name] = toFlatStr(effectiveValue(v, date, historyTrackingUnit));
      } else {
        flat[field.name] = toFlatStr(v);
      }
      if (field.allowDualValues) {
        const bKey = `${field.name}_B`;
        const bv = record[bKey];
        if (field.historyEnabled && isHistoryArray(bv)) {
          flat[bKey] = toFlatStr(effectiveValue(bv, date, historyTrackingUnit));
        } else {
          flat[bKey] = toFlatStr(bv);
        }
      }
    }
    return flat;
  }, [uiFields, historyTrackingUnit]);

  // ── Enter create mode with today's date ──────────────────────────────────
  const enterCreateMode = () => {
    const currentId = (formValues['employeeInternalId'] ?? '').trim();
    setMode('create');
    setCurrentUUID(null);
    setEmployeeRecord(null);
    setSelectedDate(normalizeDate(dayjs().format('YYYY-MM-DD')));
    setFormValues(currentId ? { employeeInternalId: currentId } : {});
    setErrors({});
    setPhotoFile(null);
    setSignFile(null);
    setPhotoPreview(null);
    setSignPreview(null);
    setDeletePhoto(false);
    setDeleteSign(false);
    setImageError('');
  };

  // ── Load employee from search ─────────────────────────────────────────────
  const loadEmployee = async (uuid: string, targetDate?: string) => {
    let record: EmployeeRecord | undefined;
    try {
      record = await getEmployee(dbKey, uuid);
    } catch { /* ignore idb error */ }

    try {
      if (!record) {
        setLoadingEmployee(true);
        setFormValues((prev) => ({ employeeInternalId: prev['employeeInternalId'] }));
        setErrors({});
        try {
          const res = await apiService.getEmployee(uuid);
          record = res.data as EmployeeRecord;
          await putEmployee(dbKey, record);
        } catch {
          enterCreateMode();
          setLoadingEmployee(false);
          return;
        }
      }
      setEmployeeRecord(record);
      setCurrentUUID(uuid);
      setMode('edit');
      // Use the provided targetDate (remembered from last session), otherwise today.
      const date = normalizeDate(targetDate ?? dayjs().format('YYYY-MM-DD'));
      lastEditStateRef.current = { internalId: String((record as any).employeeInternalId ?? ''), date };
      setSelectedDate(date);
      setFormValues(flattenRecord(record, date));
      setErrors({});
      setPhotoFile(null);
      setSignFile(null);
      setDeletePhoto(false);
      setDeleteSign(false);
      setImageError('');
      setTlScrollOffset(0);
      loadEmployeeImages(uuid);
    } catch (err: any) {
      dispatch(showNotification({ message: err.message ?? 'Failed to load employee', severity: 'error' }));
    } finally {
      setLoadingEmployee(false);
      if (pendingNavigateAfterLoadRef.current) {
        pendingNavigateAfterLoadRef.current = false;
        setTimeout(() => {
          const container = navContainerRef.current;
          if (!container || !employeeIdInputRef.current) return;
          const fields = Array.from(container.querySelectorAll<HTMLElement>('[data-nav-field]:not([data-nav-skip])'));
          const idx = fields.indexOf(employeeIdInputRef.current);
          if (idx !== -1 && idx < fields.length - 1) fields[idx + 1].focus();
        }, 50);
      }
    }
  };

  const f2MatchedEmployees = useMemo(() => {
    const q = f2SearchQuery.toLowerCase().trim();
    if (!q) return employeeList.slice(0, 50);

    const idStarts: EmployeeListItem[] = [];
    const nameStarts: EmployeeListItem[] = [];
    const nameContains: EmployeeListItem[] = [];
    const idContains: EmployeeListItem[] = [];

    for (const emp of employeeList) {
      const id = (emp.employeeInternalId ?? '').toLowerCase();
      const name = (emp.employeeName ?? '').toLowerCase();

      if (id.startsWith(q)) {
        idStarts.push(emp);
      } else if (name.startsWith(q)) {
        nameStarts.push(emp);
      } else if (name.includes(q)) {
        nameContains.push(emp);
      } else if (id.includes(q)) {
        idContains.push(emp);
      }
    }

    const result = [...idStarts, ...nameStarts, ...nameContains, ...idContains];
    return result.slice(0, 50);
  }, [f2SearchQuery, employeeList]);

  useEffect(() => {
    if (f2DialogOpen && f2ListRef.current) {
      const selectedRow = f2ListRef.current.querySelector(`tr[data-index="${f2SelectedIndex}"]`) as HTMLElement;
      if (selectedRow) {
        selectedRow.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [f2SelectedIndex, f2DialogOpen]);

  const handleF2Select = (emp: EmployeeListItem) => {
    setF2DialogOpen(false);
    skipBlurRef.current = false;
    setFieldValue('employeeInternalId', emp.employeeInternalId);

    const rememberedDate = lastEditStateRef.current?.internalId === emp.employeeInternalId
      ? lastEditStateRef.current.date
      : undefined;

    loadEmployee(emp.employeeUUID, rememberedDate);

    setTimeout(() => {
      employeeIdInputRef.current?.focus();
    }, 10);
  };

  // ── Timeline date selection ───────────────────────────────────────────────
  const handleTimelineSelect = (date: string) => {
    if (!employeeRecord) return;
    setSelectedDate(date);
    setFormValues(flattenRecord(employeeRecord, date));
    setErrors({});
    if (lastEditStateRef.current) lastEditStateRef.current.date = date;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const e: Record<string, string> = {};
    for (const field of activeFields) {
      if (field.required && !formValues[field.name]?.trim()) {
        e[field.name] = field.errorMessage || `${field.title} is required`;
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      dispatch(showNotification({ message: 'Validation failed. Please fix the errors and try again.', severity: 'error' }));
      return;
    }
    if (mode === 'edit' && employeeRecord) {
      const isExistingDate = historyDatesForRecord().includes(selectedDate);
      if (isExistingDate) {
        // Collect history-enabled fields that are actually changing on this existing date.
        const changes: Array<{ field: UIField; oldValue: string; newValue: string }> = [];
        for (const field of activeFields) {
          if (!field.historyEnabled) continue;
          const newVal = formValues[field.name] ?? '';
          if (!newVal) continue; // empty values are not saved
          const v = employeeRecord[field.name];
          const oldVal = isHistoryArray(v) ? String(effectiveValue(v, selectedDate, historyTrackingUnit) ?? '') : '';
          if (oldVal !== newVal) {
            changes.push({ field, oldValue: oldVal, newValue: newVal });
          }
        }
        if (changes.length > 0) {
          setPendingSaveDate(selectedDate);
          setPendingHistoryChanges(changes);
          setDiffExpanded(false);
          setOverrideConfirmOpen(true);
          return;
        }
      }
    }
    performSave(selectedDate);
  };

  const hasAnyHistoryChanges = useCallback((effectiveDate: string): boolean => {
    if (!employeeRecord) return true;
    for (const field of activeFields) {
      if (!field.historyEnabled) continue;
      const newVal = formValues[field.name] ?? '';
      if (!newVal) continue;
      const v = employeeRecord[field.name];
      const oldVal = isHistoryArray(v) ? toFlatStr(effectiveValue(v, effectiveDate, historyTrackingUnit)) : '';
      if (oldVal !== newVal) return true;
    }
    return false;
  }, [employeeRecord, activeFields, formValues, historyTrackingUnit]);

  const performSave = async (effectiveDate: string) => {
    if (!validate()) return;
    setSaving(true);
    const savedEmployeeId = (formValues['employeeInternalId'] ?? '').trim();
    const historyChanges = mode === 'edit' ? hasAnyHistoryChanges(effectiveDate) : true;
    try {
      const payload: Record<string, unknown> = { effectiveDate };
      for (const field of activeFields) {
        if (formValues[field.name] !== undefined) {
          const raw = formValues[field.name];
          if (field.fieldType === 'boolean') {
            payload[field.name] = raw === 'true' ? true : raw === 'false' ? false : null;
          } else {
            payload[field.name] = (field.type === 'tag' && multiSelectTagCodes.has(field.name))
              ? raw.split(',').filter(Boolean).map(Number)
              : raw;
          }
        }
        if (field.allowDualValues) {
          const bKey = `${field.name}_B`;
          if (formValues[bKey] !== undefined) {
            payload[bKey] = formValues[bKey];
          }
        }
      }

      if (mode === 'create') {
        const res = await apiService.createEmployee(payload as any);
        const now = new Date().toISOString();
        const record: any = {
          ...res.data, // Contains employeeUUID and employeeInternalId
          createdAt: now,
          updated_at: now,
        };

        // Merge form values into the record, handling history-tracked fields
        activeFields.forEach((field) => {
          const raw = formValues[field.name];
          if (raw !== undefined) {
            let val: any = (field.type === 'tag' && multiSelectTagCodes.has(field.name))
              ? raw.split(',').filter(Boolean).map(Number)
              : raw;
            if (field.fieldType === 'boolean') {
              val = raw === 'true' ? true : raw === 'false' ? false : null;
            }
            if (field.historyEnabled) {
              record[field.name] = [{ date: effectiveDate, value: val }];
            } else {
              record[field.name] = val;
            }
          }
          if (field.allowDualValues) {
            const bKey = `${field.name}_B`;
            if (formValues[bKey] !== undefined) {
              record[bKey] = formValues[bKey];
            }
          }
        });

        for (const f of res.ignoredFields ?? []) { delete record[f]; }
        await putEmployee(dbKey, record as EmployeeRecord);
        dispatch(upsertEmployeeListItem(toListItem(record as EmployeeRecord)));
        await uploadPendingImages(String(record.employeeUUID ?? ''));
        dispatch(showNotification({ message: 'Employee created successfully', severity: 'success' }));
        if (res.warnings?.length) {
          dispatch(showNotification({ message: `Some fields were ignored: ${res.warnings.join(' | ')}`, severity: 'warning' }));
        }
      } else if (mode === 'edit' && currentUUID) {
        const updateRes = await apiService.updateEmployee(currentUUID, payload as any);
        const record = updateRes.data as EmployeeRecord;
        for (const f of updateRes.ignoredFields ?? []) { delete record[f]; }
        await putEmployee(dbKey, record);
        dispatch(upsertEmployeeListItem(toListItem(record)));
        await uploadPendingImages(currentUUID);
        dispatch(showNotification({ message: 'Employee updated successfully', severity: 'success' }));
        if (updateRes.warnings?.length) {
          dispatch(showNotification({ message: `Some fields were ignored: ${updateRes.warnings.join(' | ')}`, severity: 'warning' }));
        }
        if (!historyChanges) {
          dispatch(showNotification({ message: 'No history enabled data is changed, so no new version is created.', severity: 'info' }));
        }
      }

      // Re-fetch employee tags so the UI receives updated lock statuses from the backend
      dispatch(fetchEmployeeTags());

      // Reset form, keep employee ID, focus it
      setMode(null);
      setCurrentUUID(null);
      setEmployeeRecord(null);
      setSelectedDate('');
      setFormValues(savedEmployeeId ? { employeeInternalId: savedEmployeeId } : {});
      setErrors({});
      setPhotoFile(null);
      setSignFile(null);
      setPhotoPreview(null);
      setSignPreview(null);
      setDeletePhoto(false);
      setDeleteSign(false);
      setTlScrollOffset(0);
      setTimeout(() => {
        if (employeeIdInputRef.current) employeeIdInputRef.current.focus();
        else saveButtonRef.current?.blur();
      }, 50);
    } catch (err: any) {
      if (err.jsonMessage) setErrors(err.jsonMessage);
      else dispatch(showNotification({ message: err.message ?? 'Save failed', severity: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const uploadPendingImages = async (uuid: string) => {
    if (photoFile) {
      const res = await apiService.getEmployeePhotoUploadUrl(uuid);
      await fetch(res.data.url, { method: 'PUT', body: photoFile, headers: { 'Content-Type': 'image/jpeg' } });
      setPhotoFile(null);
    } else if (deletePhoto) {
      try { await apiService.deleteStorageObject(`employee-images/photo_${uuid}.jpg`); } catch { /* already gone */ }
    }
    if (signFile) {
      const res = await apiService.getEmployeeSignatureUploadUrl(uuid);
      await fetch(res.data.url, { method: 'PUT', body: signFile, headers: { 'Content-Type': 'image/jpeg' } });
      setSignFile(null);
    } else if (deleteSign) {
      try { await apiService.deleteStorageObject(`employee-images/sign_${uuid}.jpg`); } catch { /* already gone */ }
    }
  };

  // ── Termination ───────────────────────────────────────────────────────────
  const openTerminationDialog = () => {
    const rec = employeeRecord as Record<string, unknown> | null;
    const stored = rec?.terminationType;
    setTermValues({
      terminationType: stored != null && stored !== '' ? String(Number(stored)) : '',
      terminationRemarks: String(rec?.terminationRemarks ?? ''),
      terminationDate: String(rec?.terminationDate ?? ''),
    });
    setTermErrors({});
    setTerminationDialogOpen(true);
  };

  const handleTerminateSave = async () => {
    const errs: Record<string, string> = {};
    if (!termValues.terminationType) errs.terminationType = 'Termination type is required';
    if (!termValues.terminationDate) errs.terminationDate = 'Termination date is required';
    if (Object.keys(errs).length > 0) { setTermErrors(errs); return; }
    if (!currentUUID) return;
    setTermSaving(true);
    try {
      const payload: Record<string, unknown> = {
        effectiveDate: dayjs().format('YYYY-MM-DD'),
        terminationType: parseInt(termValues.terminationType, 10),
        terminationRemarks: termValues.terminationRemarks,
        terminationDate: termValues.terminationDate,
      };
      const res = await apiService.updateEmployee(currentUUID, payload as any);
      const record = res.data as EmployeeRecord;
      await putEmployee(dbKey, record);
      dispatch(upsertEmployeeListItem(toListItem(record)));
      setEmployeeRecord(record);
      setTerminationDialogOpen(false);
      dispatch(showNotification({ message: 'Employee terminated', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: err.message ?? 'Failed to save termination', severity: 'error' }));
    } finally {
      setTermSaving(false);
    }
  };

  const handleReinstate = async () => {
    if (!currentUUID) return;
    setTermSaving(true);
    try {
      const payload: Record<string, unknown> = {
        effectiveDate: dayjs().format('YYYY-MM-DD'),
        __clearFields: ['terminationType', 'terminationRemarks', 'terminationDate'],
      };
      const res = await apiService.updateEmployee(currentUUID, payload as any);
      const record = res.data as EmployeeRecord;
      await putEmployee(dbKey, record);
      dispatch(upsertEmployeeListItem(toListItem(record)));
      setEmployeeRecord(record);
      setTerminationDialogOpen(false);
      dispatch(showNotification({ message: 'Employee reinstated', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: err.message ?? 'Failed to reinstate employee', severity: 'error' }));
    } finally {
      setTermSaving(false);
    }
  };

  // ── New version ───────────────────────────────────────────────────────────
  const handleNewVersion = () => {
    setNewVersionDate(dayjs());
    setNewVersionDialogOpen(true);
  };

  const handleNewVersionConfirm = () => {
    if (!newVersionDate) return;
    const dateStr = normalizeDate(newVersionDate.format('YYYY-MM-DD'));
    setNewVersionDialogOpen(false);
    setSelectedDate(dateStr);
    if (lastEditStateRef.current) lastEditStateRef.current.date = dateStr;
    dispatch(showNotification({ message: `Editing as of ${formatTimelineDate(dateStr)}. Save to create a new history entry.`, severity: 'info' }));
  };


  // ── Employee ID field blur: fetch by ID, go to edit or create ───────────
  const handleEmployeeIdBlur = () => {
    if (skipBlurRef.current) return;
    const id = (formValues['employeeInternalId'] ?? '').trim();
    if (!id) return;
    // Restore the remembered date if the same employee is re-entered, otherwise use today
    const rememberedDate = lastEditStateRef.current?.internalId === id
      ? lastEditStateRef.current.date
      : undefined;
    const found = employeeList.find((e) => e.employeeInternalId === id);
    if (found) {
      loadEmployee(found.employeeUUID, rememberedDate);
    } else {
      setLoadingEmployee(true);
      setFormValues((prev) => ({ employeeInternalId: prev['employeeInternalId'] }));
      setEmployeeRecord(null);
      setCurrentUUID(null);
      setPhotoPreview(null);
      setSignPreview(null);
      setErrors({});
      apiService.getEmployeeByInternalId(id)
        .then(async (res) => {
          const record = res.data as EmployeeRecord;
          await putEmployee(dbKey, record);
          dispatch(upsertEmployeeListItem(toListItem(record)));
          await loadEmployee(String(record.employeeUUID ?? ''), rememberedDate);
        })
        .catch((err: any) => {
          if (err.status === 404) {
            enterCreateMode();
          } else {
            dispatch(showNotification({ message: err.message ?? 'Failed to fetch employee', severity: 'error' }));
          }
        })
        .finally(() => setLoadingEmployee(false));
    }
  };

  // ── Image upload handlers ─────────────────────────────────────────────────
  const handleImageSelect = async (type: 'photo' | 'sign', file: File) => {
    setImageError('');
    try {
      const jpeg = await fileToJpeg(file);
      const maxKB = type === 'photo' ? 300 : 200;
      if (jpeg.size > maxKB * 1024) {
        setImageError(`${type === 'photo' ? 'Photo' : 'Signature'} must be under ${maxKB} KB after conversion`);
        return;
      }
      const previewUrl = URL.createObjectURL(jpeg);
      if (type === 'photo') { setPhotoPreview(previewUrl); setPhotoFile(jpeg); }
      else { setSignPreview(previewUrl); setSignFile(jpeg); }
    } catch {
      setImageError('Failed to process image');
    }
  };

  // ── Render field (always editable) ───────────────────────────────────────
  const renderField = (field: UIField, inputRef?: React.Ref<HTMLInputElement>, onSecondaryModeChange?: (active: boolean) => void) => {
    const value = formValues[field.name] ?? '';
    const error = errors[field.name];
    const inputStyle = (field.fontType === 'bijoy' && field.inputType !== 'TEXT_BOX') ? { fontFamily: 'SutonnyMJ, serif' } : {};
    const label = `${field.title}${field.required ? ' *' : ''}`;

    const rootTag = field.type === 'tag' ? employeeTags.find((t) => t.tagCode === field.name && t.parentId === 0) : null;
    const hasHistoryData = field.historyEnabled && isHistoryArray(employeeRecord?.[field.name]);
    const bKey = `${field.name}_B`;
    const hasBHistoryData = field.allowDualValues && field.historyEnabled && isHistoryArray(employeeRecord?.[bKey]);
    const hasAnyHistory = hasHistoryData || hasBHistoryData;

    const onHistoryClick = field.historyEnabled && hasAnyHistory ? () => {
      const isSecondary = secondaryActiveRef.current.has(field.name);
      if (isSecondary && hasBHistoryData) {
        setHistoryDialogField(field);
        setHistoryDialogKey(bKey);
      } else if (hasHistoryData) {
        setHistoryDialogField(field);
        setHistoryDialogKey(null);
      }
    } : undefined;

    const onEditTagListClick = (field.type === 'tag' && rootTag && rootTag.multiLevelGrouping === false) ? () => {
      setQuickGrouplessTagEditorRoot(rootTag);
      setQuickGrouplessTagEditorTitle(field.title);
      setQuickGrouplessTagEditorOpen(true);
    } : undefined;

    const savedFlat = employeeRecord ? flattenRecord(employeeRecord, selectedDate) : {};
    const currentVal = formValues[field.name] ?? '';
    const savedVal = savedFlat[field.name] ?? '';
    let isDifferent = currentVal !== savedVal;
    if (field.allowDualValues) {
      const currentValB = formValues[bKey] ?? '';
      const savedValB = savedFlat[bKey] ?? '';
      isDifferent = isDifferent || (currentValB !== savedValB);
    }
    const resetDisabled = !employeeRecord || mode !== 'edit' || !isDifferent;

    const onResetClick = () => {
      setFieldValue(field.name, savedVal);
      if (field.allowDualValues) {
        const savedValB = savedFlat[bKey] ?? '';
        setFieldValue(bKey, savedValB);
      }
    };

    const menuProps = {
      onHistoryClick,
      historyDisabled: !hasAnyHistory,
      onEditTagListClick,
      tagListTitle: field.title,
      onResetClick,
      resetDisabled,
      hideMenu: field.name === 'employeeInternalId',
    };

    const isEmployeeId = field.name === 'employeeInternalId';
    const endAdornment = isEmployeeId ? (
      <InputAdornment position="end">
        <IconButton
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={() => {
            const input = (inputRef && 'current' in inputRef) ? inputRef.current : null;
            if (input && document.activeElement === input) {
              input.blur();
            } else {
              handleEmployeeIdBlur();
            }
          }}
          size="small"
        >
          <AutorenewIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    ) : undefined;

    let fieldJsx: React.ReactNode;

    if (field.inputType === 'COMBO_BOX' && field.type === 'tag') {
      const { options, multi } = tagOptionsByCode(field.name);
      if (multi) {
        const selectedIds = (value ?? '').split(',').filter(Boolean);
        const selectedOptions = selectedIds
          .map((id) => options.find((o) => o.value === id))
          .filter((o): o is { value: string; label: string } => !!o);
        fieldJsx = (
          <NavAutocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={options}
            value={selectedOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, newVal) => setFieldValue(field.name, (newVal as typeof selectedOptions).map((o) => o.value).join(','))}
            {...menuProps}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={!!error}
                helperText={error}
                InputLabelProps={{ shrink: true }}
                inputProps={{ ...params.inputProps, style: inputStyle }}
              />
            )}
          />
        );
      } else {
        const selectedOption = options.find((o) => o.value === value) ?? null;
        fieldJsx = (
          <NavAutocomplete
            size="small"
            options={options}
            value={selectedOption}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, newVal) => setFieldValue(field.name, (newVal as typeof selectedOption)?.value ?? '')}
            {...menuProps}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={!!error}
                helperText={error}
                InputLabelProps={{ shrink: true }}
                inputProps={{ ...params.inputProps, style: inputStyle }}
              />
            )}
          />
        );
      }
    } else if (field.fieldType === 'boolean') {
      fieldJsx = (
        <FormControl size="small" fullWidth error={!!error}>
          <InputLabel shrink>{label}</InputLabel>
          <NavSelect
            label={label}
            value={value}
            onChange={(e) => setFieldValue(field.name, e.target.value as string)}
            displayEmpty
            {...menuProps}
          >
            <MenuItem value=""><em>—</em></MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </NavSelect>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
    } else if (field.inputType === 'COMBO_BOX' && field.possibleValues) {
      fieldJsx = (
        <FormControl size="small" fullWidth error={!!error}>
          <InputLabel shrink>{label}</InputLabel>
          <NavSelect
            label={label}
            value={value}
            onChange={(e) => setFieldValue(field.name, e.target.value as string)}
            displayEmpty
            {...menuProps}
          >
            <MenuItem value=""><em>—</em></MenuItem>
            {Object.entries(field.possibleValues).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </NavSelect>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
    } else if (field.inputType === 'DATE_PICKER') {
      fieldJsx = (
        <NavDatePicker
          label={label}
          format="DD/MM/YYYY"
          value={value ? dayjs(value) : null}
          onChange={(d: any) => setFieldValue(field.name, d ? d.format('YYYY-MM-DD') : '')}
          {...menuProps}
          slotProps={{
            textField: {
              size: 'small', fullWidth: true, error: !!error,
              helperText: error,
              InputLabelProps: { shrink: true },
            },
          }}
        />
      );
    } else if (field.fontType === 'bijoy') {
      fieldJsx = (
        <NavBanglaPhoneticInput
          label={label}
          InputLabelProps={{ shrink: true }}
          size="small"
          fullWidth
          error={!!error}
          value={value}
          inputRef={inputRef}
          onChange={(v) => setFieldValue(field.name, v)}
          onKeyDown={(e) => {
            if (field.name === 'employeeInternalId') {
              if (e.key === 'F2') {
                e.preventDefault();
                skipBlurRef.current = true;
                setF2AnchorEl(employeeIdInputRef.current);
                setF2SearchQuery(formValues['employeeInternalId'] ?? '');
                setF2SelectedIndex(0);
                setF2DialogOpen(true);
                setTimeout(() => f2ListRef.current?.focus(), 50);
              } else if (e.key === 'Enter') {
                e.preventDefault();
                if ((formValues['employeeInternalId'] ?? '').trim()) {
                  pendingNavigateAfterLoadRef.current = true;
                }
              }
            }
          }}
          onBlur={field.name === 'employeeInternalId' ? handleEmployeeIdBlur : undefined}
          helperText={error}
          InputProps={isEmployeeId ? { endAdornment } : undefined}
          {...menuProps}
        />
      );
    } else {
      const isLongOrDouble = field.fieldType === 'long' || field.fieldType === 'double';
      const isNumeric = field.minValue != null || field.maxValue != null || isLongOrDouble;

      if (field.fieldType === 'long') {
        const secondaryKey = field.allowDualValues ? `${field.name}_B` : undefined;
        fieldJsx = (
          <NavLongField
            label={label}
            size="small"
            fullWidth
            error={!!error}
            value={value}
            {...(field.allowDualValues && {
              allowDualValue: true,
              secondaryValue: formValues[secondaryKey!] ?? '',
              onSecondaryChange: (v: string) => setFieldValue(secondaryKey!, v),
              onSecondaryModeChange,
            })}
            inputRef={inputRef}
            inputProps={{ style: inputStyle }}
            onChange={(v: string) => {
              if (field.preventInvalidInput && field.pattern) {
                try {
                  const re = new RegExp(field.pattern);
                  if (v !== '' && !re.test(v)) return;
                } catch { /* ignore bad regex */ }
              }
              setFieldValue(field.name, v);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (field.name === 'employeeInternalId') {
                if (e.key === 'F2') {
                  e.preventDefault();
                  skipBlurRef.current = true;
                  setF2AnchorEl(employeeIdInputRef.current);
                  setF2SearchQuery(formValues['employeeInternalId'] ?? '');
                  setF2SelectedIndex(0);
                  setF2DialogOpen(true);
                  setTimeout(() => f2ListRef.current?.focus(), 50);
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if ((formValues['employeeInternalId'] ?? '').trim()) {
                    pendingNavigateAfterLoadRef.current = true;
                  }
                }
              }
            }}
            onBlur={field.name === 'employeeInternalId' ? handleEmployeeIdBlur : undefined}
            helperText={error}
            slotProps={{ inputLabel: { shrink: true } }}
            InputProps={isEmployeeId ? { endAdornment } : undefined}
            {...menuProps}
          />
        );
      } else if (field.fieldType === 'double') {
        const secondaryKey = field.allowDualValues ? `${field.name}_B` : undefined;
        fieldJsx = (
          <NavDoubleField
            label={label}
            size="small"
            fullWidth
            error={!!error}
            value={value}
            {...(field.allowDualValues && {
              allowDualValue: true,
              secondaryValue: formValues[secondaryKey!] ?? '',
              onSecondaryChange: (v: string) => setFieldValue(secondaryKey!, v),
              onSecondaryModeChange,
            })}
            inputRef={inputRef}
            inputProps={{ style: inputStyle }}
            onChange={(v: string) => {
              if (field.preventInvalidInput && field.pattern) {
                try {
                  const re = new RegExp(field.pattern);
                  if (v !== '' && !re.test(v)) return;
                } catch { /* ignore bad regex */ }
              }
              setFieldValue(field.name, v);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (field.name === 'employeeInternalId') {
                if (e.key === 'F2') {
                  e.preventDefault();
                  skipBlurRef.current = true;
                  setF2AnchorEl(employeeIdInputRef.current);
                  setF2SearchQuery(formValues['employeeInternalId'] ?? '');
                  setF2SelectedIndex(0);
                  setF2DialogOpen(true);
                  setTimeout(() => f2ListRef.current?.focus(), 50);
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if ((formValues['employeeInternalId'] ?? '').trim()) {
                    pendingNavigateAfterLoadRef.current = true;
                  }
                }
              }
            }}
            onBlur={field.name === 'employeeInternalId' ? handleEmployeeIdBlur : undefined}
            helperText={error}
            slotProps={{ inputLabel: { shrink: true } }}
            InputProps={isEmployeeId ? { endAdornment } : undefined}
            {...menuProps}
          />
        );
      } else {
        fieldJsx = (
          <NavTextField
            navFieldType={isNumeric ? 'number' : 'text'}
            label={label}
            size="small"
            fullWidth
            error={!!error}
            type={isNumeric ? 'number' : 'text'}
            value={value}
            inputRef={inputRef}
            inputProps={{ style: inputStyle }}
            onChange={(e) => {
              let v = e.target.value;
              if (field.preventInvalidInput && field.pattern) {
                try {
                  const re = new RegExp(field.pattern);
                  if (v !== '' && !re.test(v)) return;
                } catch { /* ignore bad regex */ }
              }
              setFieldValue(field.name, v);
            }}
            onKeyDown={(e) => {
              if (field.name === 'employeeInternalId') {
                if (e.key === 'F2') {
                  e.preventDefault();
                  skipBlurRef.current = true;
                  setF2AnchorEl(employeeIdInputRef.current);
                  setF2SearchQuery(formValues['employeeInternalId'] ?? '');
                  setF2SelectedIndex(0);
                  setF2DialogOpen(true);
                  setTimeout(() => f2ListRef.current?.focus(), 50);
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if ((formValues['employeeInternalId'] ?? '').trim()) {
                    pendingNavigateAfterLoadRef.current = true;
                  }
                }
              }
            }}
            onBlur={field.name === 'employeeInternalId' ? handleEmployeeIdBlur : undefined}
            helperText={error}
            slotProps={{ inputLabel: { shrink: true } }}
            InputProps={isEmployeeId ? { endAdornment } : undefined}
            {...menuProps}
          />
        );
      }
    }

    return fieldJsx;
  };


  // Sorted newest-first for the left timeline panel
  const sortedHistoryDates = useMemo(() => [...historyDatesForRecord()].reverse(), [historyDatesForRecord]);

  // Calculated Values — only relevant when a trigger field is focused
  // Pass {} when employeeRecord is null (create mode) so current formValues still resolve;
  // P/D/M/Y refs will simply return null (missing) for new employees.
  const evalContext = useMemo(
    () => buildEvalContext(
      (employeeRecord ?? {}) as Record<string, unknown>,
      formValues,
      selectedDate ?? '',
      sortedHistoryDates,
    ),
    [employeeRecord, formValues, selectedDate, sortedHistoryDates],
  );

  const activeCalcValues = useMemo(() => {
    return calculatedValueDefinitions
      .map((d) => ({ label: d.label, value: evaluateDSL(d.code, evalContext) }));
  }, [evalContext, calculatedValueDefinitions]);

  if (uiFields.length === 0) {
    return (
      <DashboardLayout>
        <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', m: -3, minHeight: 'calc(100vh - 64px)' }}>
        <Box sx={{ px: 3, pt: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">HR</Typography>
            <Typography variant="body2" color="text.primary" fontWeight={600}>Employee Entry</Typography>
          </Breadcrumbs>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Employee Entry</Typography>
          </Box>
        </Box>

        {/* ── Outer row: Employee Entry box → drawer → handle (flush right) ── */}
        <Box sx={{ display: 'flex', ml: { xs: 1, sm: 3 }, mr: 0, alignItems: 'flex-start' }}>

          {/* ── Two-panel layout (always visible) ── */}
          <Box sx={{
            display: 'flex',
            flex: 1,
            minWidth: 0,
            height: { xs: 'auto', sm: 'calc(100vh - 230px)' },
            minHeight: { xs: 0, sm: undefined },
            overflow: { xs: 'visible', sm: 'hidden' },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}>
            {/* Left panel: history timeline */}
            <Box sx={{
              width: isMobile ? (mobileHistoryOpen ? '100%' : 0) : 237,
              flexShrink: 0,
              borderRight: (!isMobile || mobileHistoryOpen) ? '1px solid' : 'none',
              borderColor: 'divider',
              p: (!isMobile || mobileHistoryOpen) ? 1.5 : 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              overflowX: 'hidden',
              transition: 'width 0.2s',
              position: isMobile ? 'absolute' : 'relative',
              top: isMobile ? 0 : undefined,
              left: isMobile ? 0 : undefined,
              bottom: isMobile ? 0 : undefined,
              zIndex: isMobile ? 10 : undefined,
              bgcolor: 'background.paper',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary' }}>
                  History
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="New Version">
                    <span style={{ visibility: mode === 'edit' ? 'visible' : 'hidden' }}>
                      <IconButton size="small" onClick={handleNewVersion}><AddIcon fontSize="small" /></IconButton>
                    </span>
                  </Tooltip>
                  {isMobile && (
                    <IconButton size="small" onClick={() => setMobileHistoryOpen(false)}>
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Scrollable timeline list */}
              <Box sx={{ position: 'relative', height: 350, flexShrink: 0 }}>
                <Box
                  onScroll={(e) => setTlScrollOffset(e.currentTarget.scrollTop)}
                  sx={{
                    height: '100%', overflowY: 'auto', userSelect: 'none',
                    '&::-webkit-scrollbar': { width: 5 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: 3 },
                    scrollbarWidth: 'thin',
                  }}
                >
                  {/* Upward arrow at top of line */}
                  <Box sx={{
                    position: 'sticky', top: 0, left: 16, transform: 'translateX(-50%)',
                    width: 0, height: 0,
                    borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                    borderBottom: '8px solid', borderBottomColor: '#e0e0e0', zIndex: 0,
                  }} />

                  {sortedHistoryDates.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, px: 1 }}>
                      No history
                    </Typography>
                  ) : sortedHistoryDates.map((d) => {
                    const isSelected = d === selectedDate;
                    return (
                      <Box
                        key={d}
                        onClick={loadingEmployee ? undefined : () => handleTimelineSelect(d)}
                        sx={{
                          display: 'flex', alignItems: 'center', height: ITEM_HEIGHT,
                          cursor: loadingEmployee ? 'default' : 'pointer',
                          opacity: loadingEmployee ? 0.6 : 1,
                          '&:hover .date-label': { color: loadingEmployee ? 'inherit' : 'primary.main' }
                        }}
                      >
                        {/* Line + dot */}
                        <Box sx={{ width: 32, position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                          <Box sx={{
                            width: isSelected ? 16 : 10, height: isSelected ? 16 : 10,
                            borderRadius: '50%', bgcolor: isSelected ? 'primary.main' : '#bdbdbd',
                            zIndex: 1, transition: 'all 0.15s',
                            border: isSelected ? '2px solid' : 'none', borderColor: 'primary.main',
                          }} />
                        </Box>
                        <Typography
                          className="date-label"
                          variant="body2"
                          sx={{ flex: 1, fontWeight: isSelected ? 700 : 400, color: isSelected ? 'primary.main' : 'text.primary', transition: 'color 0.15s', fontSize: '0.82rem' }}
                        >
                          {formatTimelineDate(d)}
                        </Typography>
                      </Box>
                    );
                  })}

                  {/* Trailing line */}
                  {sortedHistoryDates.length > 0 && (
                    <Box sx={{ width: 32, height: 20, position: 'relative' }}>
                      <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                    </Box>
                  )}
                </Box>

                {/* Top fade */}
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 32, pointerEvents: 'none',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                  opacity: Math.min(1, tlScrollOffset / 40), transition: 'opacity 0.15s',
                }} />
                {/* Bottom fade */}
                <Box sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 32, pointerEvents: 'none',
                  background: 'linear-gradient(to top, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                  opacity: sortedHistoryDates.length * ITEM_HEIGHT > tlScrollOffset + 100 ? 1 : 0,
                  transition: 'opacity 0.15s',
                }} />
              </Box>

              {/* ── Calculated Values ─────────────────────────────────── */}
              {activeCalcValues.length > 0 && (
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1, px: 1.5, pb: 1, flex: 1, overflowY: 'auto' }}>
                  {activeCalcValues.map(({ label, value }) => (
                    <Box key={label} sx={{ mb: 0.75 }}>
                      <Typography variant="caption" fontWeight={700} sx={{ display: 'inline' }}>
                        {label} :{' '}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'inline', wordBreak: 'break-word' }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Right panel: form */}
            <Box ref={formPanelRef} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', sm: 'hidden' }, position: 'relative' }}>
              {/* Loading overlay */}
              {showLoadingOverlay && (
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(2px)' }}>
                  <CircularProgress />
                </Box>
              )}
              {/* Fixed header bar */}
              <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', minHeight: '24.5px' }}>
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    {mode === 'edit'
                      ? (historyDatesForRecord().includes(selectedDate) ? 'Version' : (historyTrackingUnit === 'month' ? 'Effective Month' : 'Effective Date'))
                      : mode === 'create' ? 'New Employee' : ''}
                  </Box>
                  {mode !== null && (
                    <>
                      {(mode === 'edit' || mode === 'create') && ' | '}
                      {formatTimelineDate(selectedDate || dayjs().format('YYYY-MM-DD'))}
                    </>
                  )}
                  {mode === 'edit' && currentUUID && (
                    <Tooltip title="Refresh from server">
                      <IconButton
                        size="small"
                        sx={{ p: 0.25 }}
                        disabled={loadingEmployee}
                        onClick={() => {
                          setLoadingEmployee(true);
                          apiService.getEmployee(currentUUID)
                            .then(async (res) => {
                              const record = res.data as EmployeeRecord;
                              await putEmployee(dbKey, record);
                              setEmployeeRecord(record);
                              const date = normalizeDate(dayjs().format('YYYY-MM-DD'));
                              setSelectedDate(date);
                              setFormValues(flattenRecord(record, date));
                            })
                            .catch((err: any) => {
                              dispatch(showNotification({ message: err.message ?? 'Failed to refresh employee', severity: 'error' }));
                            })
                            .finally(() => setLoadingEmployee(false));
                        }}
                      >
                        {loadingEmployee
                          ? <CircularProgress size={14} />
                          : <CloudDownloadOutlinedIcon sx={{ fontSize: '1.14rem' }} color="primary" />
                        }
                      </IconButton>
                    </Tooltip>
                  )}
                  {(mode === 'create' || mode === 'edit') && (
                    <Tooltip title={`Change effective ${historyTrackingUnit === 'month' ? 'month' : 'date'}`}>
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => { setEditDateValue(dayjs(selectedDate)); setEditDateDialogOpen(true); }}>
                        <EditCalendarIcon sx={{ fontSize: '0.95rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {mode === 'edit' && employeeRecord && !historyDatesForRecord().includes(selectedDate) && (
                    <Box component="span" sx={{ ml: 0.5, color: 'text.secondary', fontSize: '0.72rem', fontWeight: 400 }}>
                      {(() => {
                        const prior = historyDatesForRecord().filter((d) =>
                          historyTrackingUnit === 'month' ? d.slice(0, 7) < selectedDate.slice(0, 7) : d < selectedDate
                        ).pop();
                        const unit = historyTrackingUnit === 'month' ? 'month' : 'date';
                        return prior
                          ? `No record for this ${unit} · showing effective version instead (${formatTimelineDate(prior)})`
                          : `No record for this ${unit} · no prior version exists`;
                      })()}
                    </Box>
                  )}
                </Typography>

                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                  {mode === 'edit' && currentUUID && (
                    <Typography variant="caption" sx={{ color: 'primary.main', userSelect: 'all', fontWeight: 500 }}>
                      {currentUUID}
                    </Typography>
                  )}
                  {(() => {
                    const tDate = String((employeeRecord as any)?.terminationDate ?? '');
                    const tType = (employeeRecord as any)?.terminationType;
                    const tRemarks = String((employeeRecord as any)?.terminationRemarks ?? '');
                    if (!tDate) return null;
                    const tooltipLines = [
                      tType != null && tType !== '' && `Type: ${termTypeLabel(tType)}`,
                      tDate && `Date: ${tDate}`,
                      tRemarks && `Remarks: ${tRemarks}`,
                    ].filter(Boolean).join('\n');
                    return (
                      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipLines}</span>} arrow>
                        <Chip
                          label="TERMINATED"
                          size="small"
                          color="error"
                          variant="filled"
                          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, cursor: 'default' }}
                        />
                      </Tooltip>
                    );
                  })()}
                  {isMobile && (
                    <Tooltip title="History">
                      <IconButton size="small" onClick={() => setMobileHistoryOpen(true)}>
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {/* Scrollable form content */}
              <Box ref={navContainerRef} sx={{
                flex: 1, overflowY: { xs: 'visible', sm: 'auto' }, p: 2,
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '3px' },
                '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9e9e9e' },
                scrollbarWidth: 'thin',
              }}>
                <FormNavigationProvider containerRef={navContainerRef} submitButtonRef={saveButtonRef}>
                  {imageError && <Alert severity="error" sx={{ mb: 2 }}>{imageError}</Alert>}

                  {/* Photo & Signature block — floated right, injected after anchor fields so
                    employeeInternalId / biometricId / employeeName appear above it */}
                  {(() => {
                    const centered = containerWidth < FORM_WIDTH_X * 0.75; // size 1 becomes 3+

                    const photoSignBlock = (
                      <Box
                        key="__photo_sign__"
                        sx={{
                          float: centered ? 'none' : 'right',
                          ml: 0,
                          mr: centered ? 0 : 2,
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          alignItems: 'center',
                          width: centered ? '100%' : undefined,
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{ position: 'relative', width: 120, height: 150, mb: 1, '&:hover .clear-btn': { opacity: 0.7 } }}>
                            <Box sx={{ width: '100%', height: '100%', border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 1 }}>
                              <img
                                src={photoPreview ?? '/default-photo.svg'}
                                alt="Employee"
                                style={{ width: '100%', height: '100%', objectFit: photoPreview ? 'cover' : 'contain' }}
                                onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/default-photo.svg'; (e.currentTarget as HTMLImageElement).style.objectFit = 'contain'; }}
                              />
                            </Box>
                            {(photoPreview || photoFile) && (
                              <IconButton
                                className="clear-btn"
                                size="small"
                                disabled={loadingEmployee}
                                onClick={() => { setPhotoPreview(null); setPhotoFile(null); setDeletePhoto(true); }}
                                sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, bgcolor: 'background.paper', '&:hover': { opacity: 1, bgcolor: 'background.paper' }, p: 0.25, transition: 'opacity 0.2s' }}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </Box>
                          <Button size="small" variant="outlined" component="label" fullWidth>
                            Upload Photo
                            <input type="file" hidden accept=".jpg,.jpeg,.png,.heif" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageSelect('photo', f); e.target.value = ''; }} />
                          </Button>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{ position: 'relative', width: 160, height: 80, mb: 1, '&:hover .clear-btn': { opacity: 0.7 } }}>
                            <Box sx={{ width: '100%', height: '100%', border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 1 }}>
                              <img
                                src={signPreview ?? '/default-signature.svg'}
                                alt="Signature"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/default-signature.svg'; }}
                              />
                            </Box>
                            {(signPreview || signFile) && (
                              <IconButton
                                className="clear-btn"
                                size="small"
                                disabled={loadingEmployee}
                                onClick={() => { setSignPreview(null); setSignFile(null); setDeleteSign(true); }}
                                sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, bgcolor: 'background.paper', '&:hover': { opacity: 1, bgcolor: 'background.paper' }, p: 0.25, transition: 'opacity 0.2s' }}
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </Box>
                          <Button size="small" variant="outlined" component="label" fullWidth>
                            Upload Signature
                            <input type="file" hidden accept=".jpg,.jpeg,.png,.heif" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageSelect('sign', f); e.target.value = ''; }} />
                          </Button>
                        </Box>
                      </Box>
                    );

                    // Render anchor fields first, then inject photo block, then the rest
                    const anchorIdx = Math.max(
                      activeFields.findIndex((f) => f.name === 'employeeName'),
                      activeFields.findIndex((f) => f.name === 'biometricId'),
                      activeFields.findIndex((f) => f.name === 'employeeInternalId'),
                    );
                    const splitAt = anchorIdx >= 0 ? anchorIdx + 1 : 0;
                    const before = activeFields.slice(0, splitAt);
                    const after = activeFields.slice(splitAt);

                    const renderFieldBox = (field: UIField) => {
                      const effSize = effectiveSize(field.size, containerWidth);
                      const cols = gridSize(effSize);
                      const pct = (cols / 12) * 100;
                      const onSecondaryModeChange = field.allowDualValues
                        ? (active: boolean) => {
                            if (active) secondaryActiveRef.current.add(field.name);
                            else secondaryActiveRef.current.delete(field.name);
                          }
                        : undefined;
                      return (
                        <Box
                          key={field.name}
                          sx={{
                            float: 'left',
                            width: `${pct}%`,
                            pr: 2,
                            pb: 2,
                            boxSizing: 'border-box',
                            ...(field.newline && { clear: 'left' }),
                          }}
                        >
                          {renderField(field, field.name === 'employeeInternalId' ? employeeIdInputRef : undefined, onSecondaryModeChange)}
                        </Box>
                      );
                    };

                    return [
                      ...before.map(renderFieldBox),
                      photoSignBlock,
                      ...after.map(renderFieldBox),
                    ];
                  })()}
                  <Box sx={{ clear: 'both' }} />
                </FormNavigationProvider>
              </Box>
            </Box>
          </Box>{/* ── end two-panel layout ── */}

          {/* ── Pull-tab handle: between entry box and drawer ─────────────────── */}
          {/* Closed → handle at browser right edge. Open → drawer appears to its right. */}
          {!isMobile && (
            <Box
              onClick={() => setFilterDrawerOpen((o) => !o)}
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '11px',
                width: 18, height: 100,
                ml: '5px',
                alignSelf: 'center',
                flexShrink: 0,
                bgcolor: 'background.paper',
                border: '1px solid', borderColor: 'divider',
                borderRight: 'none',
                borderRadius: '4px 0 0 4px',
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
                transition: 'background-color 0.15s, color 0.15s',
              }}
            >
              {filterDrawerOpen ? <ChevronRightIcon sx={{ fontSize: 12 }} /> : <ChevronLeftIcon sx={{ fontSize: 12 }} />}
              <SearchIcon sx={{ fontSize: 12, color: 'primary.main' }} />
              {filterDrawerOpen ? <ChevronRightIcon sx={{ fontSize: 12 }} /> : <ChevronLeftIcon sx={{ fontSize: 12 }} />}
            </Box>
          )}

          {/* ── Filter drawer panel ──────────────────────────────────────────── */}
          {!isMobile && (
            <Box sx={{
              width: filterDrawerOpen ? drawerWidth : 0,
              flexShrink: 0,
              height: { sm: 'calc(100vh - 230px)' },
              overflow: 'hidden',
              pointerEvents: filterDrawerOpen ? 'auto' : 'none',
              border: filterDrawerOpen ? '1px solid' : 'none',
              borderColor: 'divider',
              borderRadius: '0 4px 4px 0',
              bgcolor: 'background.paper',
              transition: isResizingRef.current ? 'none' : 'width 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}>
              {/* Left-edge resize grip */}
              {filterDrawerOpen && (
                <Box
                  onMouseDown={handleDrawerResizeMouseDown}
                  sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: 5,
                    cursor: 'col-resize', zIndex: 1,
                    '&:hover': { bgcolor: 'primary.main', opacity: 0.25 },
                  }}
                />
              )}

              <Box sx={{ width: drawerWidth, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                {/* Header */}
                <Box sx={{ px: 1.5, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary' }}>
                    Filter Employees
                  </Typography>
                </Box>

                {/* Filter component */}
                <Box sx={{ px: 1.5, pt: 1, flexShrink: 0 }}>
                  <EmployeeFilter uiFields={activeFields} employees={employeeRecords} onChange={handleFilterChange} />
                </Box>

                <Divider />

                {/* Results table */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 1.5 }}>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    {/* Header Table (fixed) */}
                    <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '0.72rem', py: 0.5, fontWeight: 600, width: 90, borderBottom: 'none' }}>Emp. ID</TableCell>
                            <TableCell sx={{ fontSize: '0.72rem', py: 0.5, fontWeight: 600, borderBottom: 'none' }}>Name</TableCell>
                          </TableRow>
                        </TableHead>
                      </Table>
                    </Box>
                    {/* Body Table (scrollable) */}
                    <Box
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                          e.preventDefault();
                          const available = drawerEmployees.slice(0, 300);
                          const currentIndex = available.findIndex((emp) => emp.employeeUUID === currentUUID);
                          let nextIndex = 0;
                          if (currentIndex !== -1) {
                            nextIndex = e.key === 'ArrowDown' ? Math.min(currentIndex + 1, available.length - 1) : Math.max(currentIndex - 1, 0);
                          }
                          const nextEmp = available[nextIndex];
                          if (nextEmp && nextEmp.employeeUUID !== currentUUID) {
                            loadEmployee(nextEmp.employeeUUID);
                          }
                        }
                      }}
                      sx={{
                        flex: 1, overflowX: 'hidden', overflowY: 'auto',
                        '&:focus': { outline: 'none' },
                        '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '3px' },
                        '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9e9e9e' },
                        scrollbarWidth: 'thin',
                      }}>
                      <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableBody>
                          {drawerEmployees.slice(0, 300).map((emp) => (
                            <TableRow
                              key={emp.employeeUUID}
                              hover
                              selected={emp.employeeUUID === currentUUID}
                              onClick={() => loadEmployee(emp.employeeUUID)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell sx={{ fontSize: '0.75rem', py: 0.4, width: 90 }}>{emp.employeeInternalId}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem', py: 0.4 }}>{emp.employeeName}</TableCell>
                            </TableRow>
                          ))}
                          {drawerEmployees.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={2} sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>
                                No employees match
                              </TableCell>
                            </TableRow>
                          )}
                          {drawerEmployees.length > 300 && (
                            <TableRow>
                              <TableCell colSpan={2} sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.72rem', py: 0.5 }}>
                                Showing top 300 — refine filter
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

        </Box>{/* ── end outer row ── */}

        {/* ── Bottom action bar ── */}
        <Paper elevation={6} sx={{ position: 'sticky', bottom: 0, px: 3, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1, borderRadius: 0, mt: 'auto' }}>
          {mode === 'edit' && (() => {
            const tDate = String((employeeRecord as any)?.terminationDate ?? '');
            const tType = (employeeRecord as any)?.terminationType;
            const tRemarks = String((employeeRecord as any)?.terminationRemarks ?? '');
            const isTerminated = !!tDate;
            const tooltipContent = isTerminated
              ? [
                tType != null && tType !== '' && `Type: ${termTypeLabel(tType)}`,
                tDate && `Date: ${tDate}`,
                tRemarks && `Remarks: ${tRemarks}`,
              ].filter(Boolean).join('\n')
              : '';
            return (
              <Tooltip
                title={isTerminated ? <span style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</span> : 'Terminate this employee'}
                arrow
              >
                <span style={{ marginRight: 'auto' }}>
                  <Button
                    variant="contained"
                    color={isTerminated ? 'success' : 'error'}
                    startIcon={isTerminated ? <HowToRegIcon /> : <PersonOffIcon />}
                    onClick={isTerminated ? handleReinstate : openTerminationDialog}
                    disabled={termSaving || loadingEmployee}
                  >
                    {termSaving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {isTerminated ? 'Reinstate' : 'Terminate'}
                  </Button>
                </span>
              </Tooltip>
            );
          })()}
          <Button
            ref={saveButtonRef}
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || mode === null || loadingEmployee}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                const fields = Array.from(
                  navContainerRef.current?.querySelectorAll<HTMLElement>('[data-nav-field]:not([data-nav-skip])') ?? []
                );
                fields[fields.length - 1]?.focus();
              }
            }}
          >
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Save
          </Button>
        </Paper>
      </Box>

      {/* ── Edit effective date dialog (create mode) ──────────────────────── */}
      <Dialog open={editDateDialogOpen} onClose={() => setEditDateDialogOpen(false)}>
        <DialogTitle>{historyTrackingUnit === 'month' ? 'Change Effective Month' : 'Change Effective Date'}</DialogTitle>
        <DialogContent>
          <DatePicker
            label={historyTrackingUnit === 'month' ? 'Effective Month' : 'Effective Date'}
            value={editDateValue}
            onChange={setEditDateValue}
            minDate={mode === 'edit' && employeeRecord ? dayjs(historyDatesForRecord()[0]) : undefined}
            {...(historyTrackingUnit === 'month' ? { views: ['year', 'month'] as const, openTo: 'month' as const, format: 'MM/YYYY' } : {})}
            slotProps={{ textField: { size: 'small', fullWidth: true, sx: { mt: 1 } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!editDateValue} onClick={() => {
            if (editDateValue) {
              const dateStr = normalizeDate(editDateValue.format('YYYY-MM-DD'));
              setSelectedDate(dateStr);
              if (lastEditStateRef.current) lastEditStateRef.current.date = dateStr;
              if (mode === 'edit' && employeeRecord) {
                setFormValues(flattenRecord(employeeRecord, dateStr));
                setErrors({});
              }
            }
            setEditDateDialogOpen(false);
          }}>Apply</Button>
        </DialogActions>
      </Dialog>

      {/* ── New version date dialog ────────────────────────────────────────── */}
      <Dialog open={newVersionDialogOpen} onClose={() => setNewVersionDialogOpen(false)}>
        <DialogTitle>Add New History Version</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a date for the new version.
          </Typography>
          <DatePicker
            label="New Version Date"
            value={newVersionDate}
            onChange={setNewVersionDate}
            minDate={employeeRecord ? dayjs(latestHistoryDate(employeeRecord, historyFieldNames, historyTrackingUnit) || undefined) : undefined}
            {...(historyTrackingUnit === 'month' ? { views: ['year', 'month'] as const, openTo: 'month' as const, format: 'MM/YYYY' } : {})}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewVersionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleNewVersionConfirm} disabled={!newVersionDate}>Continue</Button>
        </DialogActions>
      </Dialog>

      {/* ── Override confirmation dialog ──────────────────────────────────── */}
      <Dialog open={overrideConfirmOpen} onClose={() => setOverrideConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Version — {formatTimelineDate(pendingSaveDate)}</DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <Typography>
            On <strong>{formatTimelineDate(pendingSaveDate)}</strong>, the following history-tracked fields have different values. Saving will override them.
          </Typography>
          <Button
            size="small"
            onClick={() => setDiffExpanded((p) => !p)}
            sx={{ mt: 1, mb: 0, px: 0, textTransform: 'none' }}
          >
            {diffExpanded ? 'Hide Differences' : 'View Differences'}
          </Button>
          <Collapse in={diffExpanded}>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ maxHeight: 220, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Current ({formatTimelineDate(pendingSaveDate)})</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>New Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingHistoryChanges.map(({ field, oldValue, newValue }) => {
                    const resolve = (v: string) => {
                      if (field.type === 'tag') {
                        return v.split(',').filter(Boolean).map((id) => tagTitleById.get(id) ?? id).join(', ');
                      }
                      return field.inputType === 'COMBO_BOX' && field.possibleValues
                        ? (field.possibleValues[v] ?? v)
                        : v;
                    };
                    return (
                      <TableRow key={field.name}>
                        <TableCell>{field.title}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{resolve(oldValue) || '—'}</TableCell>
                        <TableCell sx={{ color: 'warning.main', fontWeight: 500 }}>{resolve(newValue)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </DialogContent>
        <DialogActions sx={{ pt: 1.5 }}>
          <Button onClick={() => setOverrideConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => { setOverrideConfirmOpen(false); performSave(pendingSaveDate); }}>
            Override
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── F2 Select Dialog ──────────────────────────────────────────────── */}
      <Popover
        open={f2DialogOpen}
        anchorEl={f2AnchorEl}
        onClose={() => {
          setF2DialogOpen(false);
          skipBlurRef.current = false;
          setTimeout(() => employeeIdInputRef.current?.focus(), 10);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
      >
        <Box
          ref={f2ListRef}
          tabIndex={-1}
          sx={{
            width: 400, maxHeight: 300, overflowY: 'auto', p: 0, outline: 'none',
            border: 1, borderColor: 'divider', borderRadius: 1,
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setF2SelectedIndex((prev) => Math.min(prev + 1, Math.max(0, f2MatchedEmployees.length - 1)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setF2SelectedIndex((prev) => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          onKeyUp={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (f2MatchedEmployees[f2SelectedIndex]) {
                handleF2Select(f2MatchedEmployees[f2SelectedIndex]);
              }
            }
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Employee ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Employee Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {f2MatchedEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                f2MatchedEmployees.map((emp, idx) => (
                  <TableRow
                    key={emp.employeeUUID}
                    data-index={idx}
                    hover
                    selected={idx === f2SelectedIndex}
                    onClick={() => handleF2Select(emp)}
                    sx={{
                      cursor: 'pointer',
                      '&.Mui-selected': { bgcolor: (theme) => theme.palette.primary.main + '1A' },
                      '&.Mui-selected:hover': { bgcolor: (theme) => theme.palette.primary.main + '26' },
                    }}
                  >
                    <TableCell>{emp.employeeInternalId}</TableCell>
                    <TableCell>{emp.employeeName}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Popover>

      {/* ── Field history dialog ──────────────────────────────────────────── */}
      {historyDialogField && (() => {
        const lookupKey = historyDialogKey ?? historyDialogField.name;
        const isSecondaryDialog = !!historyDialogKey;
        const entries = (employeeRecord?.[lookupKey] as HistoryEntry[] | undefined) ?? [];
        const sorted = [...entries].sort((a, b) => b.date.localeCompare(a.date));
        const dateHeader = historyTrackingUnit === 'month' ? 'Month' : 'Date';
        const closeDialog = () => { setHistoryDialogField(null); setHistoryDialogKey(null); };
        return (
          <Dialog open onClose={closeDialog} maxWidth="sm" fullWidth>
            <DialogTitle>{historyDialogField.title}{isSecondaryDialog ? ' (B)' : ''} — History</DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 140 }}>{dateHeader}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" color="text.secondary">No history recorded.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : sorted.map((e) => (
                    <TableRow key={e.date} hover>
                      <TableCell>{formatTimelineDate(e.date)}</TableCell>
                      <TableCell>
                        {historyDialogField.type === 'tag'
                          ? String(e.value ?? '').split(',').filter(Boolean).map((id) => tagTitleById.get(id) ?? id).join(', ')
                          : (historyDialogField.possibleValues?.[String(e.value)] ?? String(e.value ?? ''))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* ── Quick Groupless Tag Editor Dialog ───────────────────────────────────────── */}
      <QuickGrouplessTagEditorDialog
        open={quickGrouplessTagEditorOpen}
        onClose={() => setQuickGrouplessTagEditorOpen(false)}
        rootTag={quickGrouplessTagEditorRoot}
        fieldTitle={quickGrouplessTagEditorTitle}
      />

      {/* ── Termination dialog ───────────────────────────────────────────────── */}
      <Dialog open={terminationDialogOpen} onClose={() => setTerminationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOffIcon color="error" fontSize="small" />
          Terminate Employee
        </DialogTitle>
        <DialogContent>
          <FormNavigationProvider containerRef={termNavContainerRef} submitButtonRef={termSaveButtonRef}>
            <Box ref={termNavContainerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {/* Termination Type */}
              <FormControl size="small" fullWidth error={!!termErrors.terminationType}>
                <InputLabel shrink>Termination Type *</InputLabel>
                <NavSelect
                  label="Termination Type *"
                  value={termValues.terminationType}
                  onChange={(e) => {
                    setTermValues((p) => ({ ...p, terminationType: e.target.value as string }));
                    if (termErrors.terminationType) setTermErrors((p) => { const n = { ...p }; delete n.terminationType; return n; });
                  }}
                  displayEmpty
                >
                  <MenuItem value=""><em>— Select type —</em></MenuItem>
                  {TERMINATION_TYPES.map((t) => (
                    <MenuItem key={t.code} value={String(t.code)}>
                      <Box>
                        <Typography variant="body2">{t.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal', maxWidth: 400 }}>
                          {t.definition}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </NavSelect>
                {termErrors.terminationType && <FormHelperText>{termErrors.terminationType}</FormHelperText>}
              </FormControl>

              {/* Termination Date */}
              <NavDatePicker
                label="Termination Date *"
                format="DD/MM/YYYY"
                value={termValues.terminationDate ? dayjs(termValues.terminationDate) : null}
                onChange={(d: any) => {
                  setTermValues((p) => ({ ...p, terminationDate: d ? d.format('YYYY-MM-DD') : '' }));
                  if (termErrors.terminationDate) setTermErrors((p) => { const n = { ...p }; delete n.terminationDate; return n; });
                }}
                slotProps={{
                  textField: {
                    size: 'small', fullWidth: true,
                    error: !!termErrors.terminationDate,
                    helperText: termErrors.terminationDate,
                    InputLabelProps: { shrink: true },
                  },
                }}
              />

              {/* Termination Remarks */}
              <NavTextField
                navFieldType="text"
                label="Termination Remarks"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={termValues.terminationRemarks}
                onChange={(e) => setTermValues((p) => ({ ...p, terminationRemarks: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
          </FormNavigationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminationDialogOpen(false)}>Cancel</Button>
          <Button
            ref={termSaveButtonRef}
            variant="contained"
            color="error"
            startIcon={termSaving ? <CircularProgress size={16} /> : <PersonOffIcon />}
            onClick={handleTerminateSave}
            disabled={termSaving}
          >
            Terminate
          </Button>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
};

export default Employees;
